import { useIsVisible } from '@folklore/hooks';
import classNames from 'classnames';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import useFormCampaign from '../../hooks/useFormCampaign';
import canAnswerFormBySteps from '../../lib/canAnswerFormBySteps';

import Form from '../forms/Form';
import FormSteps from '../forms/FormSteps';
import FormTitle from '../typography/FormTitle';

import styles from '../../styles/blocks/form-campaign-block.module.css';

interface FormCampaignBlockProps {
    documentId?: string | null;
    className?: string | null;
    onEmpty?: (() => void) | null;
    onFilled?: (() => void) | null;
}

function FormCampaignBlock({
    documentId = null,
    className = null,
    onEmpty = null,
    onFilled = null,
}: FormCampaignBlockProps) {
    // Fetch only when the block gets close, so articles read halfway don't request it
    const { ref, visible } = useIsVisible({ persist: true, rootMargin: '500px' });
    const { campaign = null, isFetched = false } = useFormCampaign(documentId, {
        enabled: visible,
    });

    useEffect(() => {
        if (!isFetched) {
            return;
        }
        if (campaign !== null && onFilled !== null) {
            onFilled();
        } else if (campaign === null && onEmpty !== null) {
            onEmpty();
        }
    }, [campaign, isFetched]);

    const { label = null, heading = null, form = null } = campaign || {};

    return (
        <div className={classNames([styles.container, className])} ref={ref}>
            {form !== null ? (
                <div className={styles.inner}>
                    <div className={styles.label}>
                        {label !== null ? (
                            label
                        ) : (
                            <FormattedMessage
                                defaultMessage="Sondage"
                                description="Label of a survey inserted in an article"
                            />
                        )}
                    </div>
                    {heading !== null ? (
                        <FormTitle className={styles.heading}>{heading}</FormTitle>
                    ) : null}
                    {canAnswerFormBySteps(form) ? <FormSteps form={form} /> : <Form form={form} />}
                </div>
            ) : null}
        </div>
    );
}

export default FormCampaignBlock;
